import { ofetch } from 'ofetch';
import { gzip } from 'pako';

import { _fileStatus } from '@/api/files/mutations';
import { FileStatus, getFileFailureMessage } from '@/api/files/types';
import { emitToast } from '@/utils/emit-toast';
import { sentryCaptureFileError } from '@/utils/sentry/sentry-file-error';

export const UPLOAD_TIMEOUT_MESSAGE =
  "We couldn't confirm your upload finished. Please try again in a moment.";

const STATUS_INTERVAL = 1500;
const STATUS_MAX_ATTEMPTS = 10; // ~15s — covers SQS lag on the BE validator

type UploadToS3Options = {
  uploadUrl: string;
  uploadHeaders: Record<string, string>;
  file: File;
  fileId: string;
  compress?: boolean;
  /** Set false when the caller reports the failure itself, so one failure raises one toast. */
  emitToasts?: boolean;
};

/**
 * Thrown when the BE validator rejects a completed upload (size cap exceeded,
 * MIME spoof, magic-byte mismatch, blocked extension). The `code` is the BE's
 * structured FileErrorCodes value; `message` is already user-friendly.
 */
export class FileUploadRejectedError extends Error {
  readonly code?: string;
  constructor(code: string | undefined, message: string) {
    super(message);
    this.name = 'FileUploadRejectedError';
    this.code = code;
  }
}

/**
 * Thrown when the BE never transitions the upload to Uploaded/Failed within the
 * polling window. Surfaced as a user-facing toast so the upload doesn't fail
 * silently when the validator is lagging.
 */
export class FileUploadTimeoutError extends Error {
  constructor(fileId: string) {
    super(`File ${fileId} status check timed out`);
    this.name = 'FileUploadTimeoutError';
  }
}

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

const waitForFileUploaded = async (fileId: string): Promise<void> => {
  for (let attempt = 0; attempt < STATUS_MAX_ATTEMPTS; attempt++) {
    const { status, failureCode } = await _fileStatus(fileId);

    if (status === FileStatus.UPLOADED) {
      return;
    }

    if (status === FileStatus.FAILED) {
      throw new FileUploadRejectedError(failureCode, getFileFailureMessage(failureCode));
    }

    if (status === FileStatus.NOT_FOUND) {
      throw new Error(`File ${fileId} not found`);
    }

    await sleep(STATUS_INTERVAL);
  }

  throw new FileUploadTimeoutError(fileId);
};

export const uploadToS3 = async ({
  uploadUrl,
  uploadHeaders,
  file,
  fileId,
  compress = false,
  emitToasts = true,
}: UploadToS3Options): Promise<void> => {
  const contentType = uploadHeaders?.['Content-Type'] ?? (file.type || 'application/octet-stream');

  let body: Uint8Array | File = file;
  const headers: Record<string, string> = {
    ...uploadHeaders,
    'Content-Type': contentType,
  };

  if (compress) {
    const arrayBuffer = await file.arrayBuffer();
    body = gzip(new Uint8Array(arrayBuffer));
    headers['Content-Encoding'] = 'gzip';
  }

  try {
    await ofetch(uploadUrl, {
      method: 'PUT',
      headers,
      body,
    });

    await waitForFileUploaded(fileId);
  } catch (err) {
    if (err instanceof FileUploadRejectedError) {
      if (emitToasts) {
        emitToast({
          type: 'error',
          title: 'Upload rejected',
          message: err.message,
        });
      }
      throw err;
    }

    if (err instanceof FileUploadTimeoutError) {
      if (emitToasts) {
        emitToast({
          type: 'error',
          title: 'Upload timed out',
          message: UPLOAD_TIMEOUT_MESSAGE,
        });
      }
      throw err;
    }

    const status = (err as { status?: number })?.status;
    sentryCaptureFileError({
      operation: 'upload',
      error: err instanceof Error ? err : new Error(String(err)),
      filename: file.name,
      fileSize: file.size,
      contentType,
      url: uploadUrl,
      status,
    });
    throw err;
  }
};
